import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useClinicSettings } from '../contexts/ClinicSettingsContext';

interface ClinicSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const MAX_LOGO_SIZE = 500 * 1024; // 500KB

const ClinicSettingsModal: React.FC<ClinicSettingsModalProps> = ({ isOpen, onClose }) => {
    const { settings, updateSettings, isLoading: contextLoading, error: contextError } = useClinicSettings();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        clinicName: settings.clinicName || '',
        doctorName: settings.doctorName || '',
        clinicAddress: settings.clinicAddress || '',
        clinicPhone: settings.clinicPhone || '',
        clinicEmail: settings.clinicEmail || '',
        clinicWebsite: settings.clinicWebsite || '',
    });
    const [logoPreview, setLogoPreview] = useState<string | null>(
        settings.clinicLogoSignedUrl || (
            settings.clinicLogoBase64
                ? `data:${settings.clinicLogoMimeType || 'image/png'};base64,${settings.clinicLogoBase64}`
                : null)
    );
    const [logoBase64, setLogoBase64] = useState(settings.clinicLogoBase64 || '');
    const [logoMimeType, setLogoMimeType] = useState(settings.clinicLogoMimeType || '');
    const [logoError, setLogoError] = useState<string | null>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLogoError(null);

        if (file.size > MAX_LOGO_SIZE) {
            setLogoError(`Logo file is too large (${(file.size / 1024).toFixed(0)}KB). Maximum size is 500KB.`);
            return;
        }

        if (!file.type.startsWith('image/')) {
            setLogoError('Please select a valid image file (PNG, JPG, SVG, etc.)');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            setLogoPreview(dataUrl);

            // Extract base64 and mime type
            const [header, base64Data] = dataUrl.split(',');
            const mimeMatch = header.match(/data:([^;]+)/);
            setLogoBase64(base64Data);
            setLogoMimeType(mimeMatch?.[1] || 'image/png');
        };
        reader.readAsDataURL(file);
    }, []);

    const handleRemoveLogo = () => {
        setLogoPreview(null);
        setLogoBase64('');
        setLogoMimeType('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);
        try {
            await updateSettings({
                ...formData,
                clinicLogoBase64: logoBase64 || undefined,
                clinicLogoMimeType: logoMimeType || undefined,
            });
            onClose();
        } catch (e: any) {
            setSaveError(e.message || 'Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to saved settings
        setFormData({
            clinicName: settings.clinicName || '',
            doctorName: settings.doctorName || '',
            clinicAddress: settings.clinicAddress || '',
            clinicPhone: settings.clinicPhone || '',
            clinicEmail: settings.clinicEmail || '',
            clinicWebsite: settings.clinicWebsite || '',
        });
        setLogoPreview(
            settings.clinicLogoSignedUrl || (
                settings.clinicLogoBase64
                    ? `data:${settings.clinicLogoMimeType || 'image/png'};base64,${settings.clinicLogoBase64}`
                    : null)
        );
        setLogoBase64(settings.clinicLogoBase64 || '');
        setLogoMimeType(settings.clinicLogoMimeType || '');
        setLogoError(null);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    onClick={handleCancel}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2 }}
                        className="w-full max-w-lg bg-zinc-900 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-white">Clinic Settings</h2>
                                <p className="text-sm text-zinc-400">Customize your PDF report branding</p>
                            </div>
                            <button
                                onClick={handleCancel}
                                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                            {/* Logo Upload */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-zinc-300">Clinic Logo</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-20 h-20 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
                                        {logoPreview ? (
                                            <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-contain" />
                                        ) : (
                                            <span className="text-xs text-zinc-500 text-center">No Logo</span>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-500 rounded-lg transition-colors"
                                        >
                                            Upload Logo
                                        </button>
                                        {logoPreview && (
                                            <button
                                                onClick={handleRemoveLogo}
                                                className="px-4 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {logoError && <p className="text-sm text-red-400">{logoError}</p>}
                                <p className="text-xs text-zinc-500">Max 500KB. Recommended: square image, PNG or SVG.</p>
                            </div>

                            {/* Clinic Name */}
                            <div className="space-y-1">
                                <label className="block text-sm font-medium text-zinc-300">Clinic Name</label>
                                <input
                                    type="text"
                                    name="clinicName"
                                    value={formData.clinicName}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Bright Smile Dental"
                                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Doctor Name */}
                            <div className="space-y-1">
                                <label className="block text-sm font-medium text-zinc-300">Doctor / Practitioner Name</label>
                                <input
                                    type="text"
                                    name="doctorName"
                                    value={formData.doctorName}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Dr. Jane Smith"
                                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Address */}
                            <div className="space-y-1">
                                <label className="block text-sm font-medium text-zinc-300">Address</label>
                                <input
                                    type="text"
                                    name="clinicAddress"
                                    value={formData.clinicAddress}
                                    onChange={handleInputChange}
                                    placeholder="e.g., 123 Main St, Suite 100, City, State 12345"
                                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Phone & Email Row */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className="block text-sm font-medium text-zinc-300">Phone</label>
                                    <input
                                        type="tel"
                                        name="clinicPhone"
                                        value={formData.clinicPhone}
                                        onChange={handleInputChange}
                                        placeholder="(555) 123-4567"
                                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-sm font-medium text-zinc-300">Email</label>
                                    <input
                                        type="email"
                                        name="clinicEmail"
                                        value={formData.clinicEmail}
                                        onChange={handleInputChange}
                                        placeholder="contact@clinic.com"
                                        className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            {/* Website */}
                            <div className="space-y-1">
                                <label className="block text-sm font-medium text-zinc-300">Website (Optional)</label>
                                <input
                                    type="url"
                                    name="clinicWebsite"
                                    value={formData.clinicWebsite}
                                    onChange={handleInputChange}
                                    placeholder="https://www.yourpractice.com"
                                    className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-white/10">
                            {saveError && (
                                <p className="text-sm text-red-400 mb-3 text-center">{saveError}</p>
                            )}
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    className="px-5 py-2.5 text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="px-5 py-2.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isSaving ? (
                                        <>
                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Settings'
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ClinicSettingsModal;
